<template>
  <div class="halaman-admin">
    <div class="dashboard-container">
      <!-- Header Section -->
      <div class="header-section mb-3">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 class="page-main-title mb-1">
              <i class="bi bi-trophy text-primary me-2"></i>Turnamen
            </h4>
            <p class="text-muted mb-0 small">
              Pilih kejuaraan yang ingin diikuti kontingen Anda, pantau status keikutsertaan, dan unduh petunjuk teknis.
            </p>
          </div>
        </div>
      </div>

      <!-- Memuat Skeleton -->
      <div v-if="memuat" class="poster-turnamen-grid">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-poster"></div>
          <div class="poster-card__body">
            <div class="skeleton-line" style="width: 40%; height: 18px; margin-bottom: 8px;"></div>
            <div class="skeleton-line" style="width: 80%; height: 22px; margin-bottom: 12px;"></div>
            <div class="skeleton-line" style="width: 100%; height: 65px; margin-bottom: 12px;"></div>
            <div class="skeleton-line" style="width: 100%; height: 38px; margin-bottom: 8px;"></div>
            <div class="d-flex gap-2">
              <div class="skeleton-line" style="flex: 1; height: 32px;"></div>
              <div class="skeleton-line" style="flex: 1; height: 32px;"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Belum punya kontingen sama sekali -->
      <div v-else-if="!kontingen.length" class="empty-box text-center py-5">
        <div class="empty-icon-circle mb-3">
          <i class="bi bi-people text-primary fs-2"></i>
        </div>
        <h5 class="fw-bold text-dark mb-1">Anda Belum Memiliki Kontingen</h5>
        <p class="text-muted small mb-3" style="max-width: 400px; margin: 0 auto;">
          Buat profil kontingen/perguruan Anda terlebih dahulu agar dapat mendaftarkan atlet ke turnamen yang tersedia.
        </p>
        <router-link to="/admin/contingent/create" class="btn btn-primary btn-sm px-3 shadow-sm text-white">
          <i class="bi bi-plus-lg me-1"></i> Buat Kontingen Baru
        </router-link>
      </div>

      <!-- Tidak ada acara turnamen -->
      <div v-else-if="!turnamen.length" class="empty-box text-center py-5">
        <div class="empty-icon-circle mb-3">
          <i class="bi bi-calendar-x text-muted fs-2"></i>
        </div>
        <h5 class="fw-bold text-dark mb-1">Belum Ada Turnamen Dibuka</h5>
        <p class="text-muted small mb-0">Turnamen yang dibuka panitia akan otomatis muncul di halaman ini.</p>
      </div>

      <!-- Daftar Acara Turnamen Cards (Full Portrait Showcase) -->
      <div v-else class="poster-turnamen-grid">
        <article v-for="t in turnamen" :key="t.id" class="poster-card">
          <!-- Poster Container (Full Portrait) -->
          <div
            class="poster-card__media"
            :class="{ 'has-image': !!gambar(t) }"
            :title="gambar(t) ? 'Klik untuk melihat poster penuh' : ''"
            @click="gambar(t) && bukaPoster(t)"
          >
            <img
              v-if="gambar(t)"
              :src="gambar(t)"
              :alt="t.name"
              class="poster-card__img"
              loading="eager"
            />
            <div v-else class="poster-card__placeholder">
              <i class="bi bi-trophy text-white-50 fs-1"></i>
              <span class="text-white-50 small mt-2">Belum ada poster</span>
            </div>

            <!-- Hover overlay hint -->
            <div v-if="gambar(t)" class="poster-card__overlay">
              <span class="poster-card__zoom-btn">
                <i class="bi bi-arrows-fullscreen me-1"></i> Lihat Poster
              </span>
            </div>

            <!-- Floating Status Badge (Top Right) -->
            <div class="floating-badge-top-right">
              <span :class="['mini-status-badge', `status--${infoStatus(t).tone}`]">
                <span class="status-dot"></span>
                {{ $t(infoStatus(t).kunci) }}
              </span>
            </div>
          </div>

          <!-- Card Body -->
          <div class="poster-card__body">
            <!-- Header: Tag Kejuaraan & Countdown Sisa Hari -->
            <div class="poster-card__header">
              <div class="d-flex align-items-center justify-content-between gap-2 mb-1 flex-wrap">
                <span class="badge-kejuaraan-resmi">
                  <i class="bi bi-shield-check me-1 text-success"></i>Kejuaraan Resmi
                </span>
                <span v-if="sisa(t)" class="badge-sisa-hari" :class="{ 'is-urgent': mendesak(t) }">
                  <i class="bi bi-hourglass-split me-1"></i>{{ sisa(t) }}
                </span>
              </div>
              <h4 class="poster-card__title" :title="t.name">
                {{ t.name }}
              </h4>
            </div>

            <!-- Informasi Penting -->
            <div class="poster-card__info-list">
              <div v-if="t.event_date" class="info-row">
                <div class="info-row__icon text-primary">
                  <i class="bi bi-calendar2-event-fill"></i>
                </div>
                <div class="info-row__content">
                  <span class="info-row__label">Pelaksanaan</span>
                  <span class="info-row__value">{{ t.event_date }}</span>
                </div>
              </div>

              <div v-if="t.location" class="info-row">
                <div class="info-row__icon text-danger">
                  <i class="bi bi-geo-alt-fill"></i>
                </div>
                <div class="info-row__content">
                  <span class="info-row__label">Lokasi</span>
                  <span class="info-row__value">{{ t.location }}</span>
                </div>
              </div>

              <div v-if="t.registration_deadline" class="info-row">
                <div class="info-row__icon text-warning">
                  <i class="bi bi-clock-history"></i>
                </div>
                <div class="info-row__content">
                  <span class="info-row__label">Batas Pendaftaran</span>
                  <span class="info-row__value">{{ formatTanggal(t.registration_deadline) }}</span>
                </div>
              </div>
            </div>

            <!-- Action Buttons Footer -->
            <div class="poster-card__footer">
              <a
                :href="`/detail/${t.slug}`"
                target="_blank"
                rel="noopener"
                class="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
                title="Lihat Detail Turnamen"
              >
                <i class="bi bi-eye-fill"></i>
                <span>Detail Turnamen</span>
              </a>

              <!-- Tombol Grup WhatsApp -->
              <button
                type="button"
                class="btn btn-wa w-100 d-inline-flex align-items-center justify-content-center gap-2"
                title="Gabung Grup WhatsApp Turnamen"
                @click="klikGrupWA(t)"
              >
                <i class="bi bi-whatsapp"></i>
                <span>Grup WhatsApp</span>
              </button>

              <div class="d-flex gap-2 w-100 flex-wrap">
                <button
                  v-if="gambar(t)"
                  type="button"
                  class="btn btn-sm btn-outline-secondary flex-fill d-inline-flex align-items-center justify-content-center gap-1"
                  title="Lihat Poster Ukuran Penuh"
                  @click="bukaPoster(t)"
                >
                  <i class="bi bi-image text-primary"></i>
                  <span>Poster</span>
                </button>

                <a
                  v-if="adaProposal(t)"
                  :href="urlProposal(t)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-sm btn-outline-secondary flex-fill d-inline-flex align-items-center justify-content-center gap-1"
                  :class="{
                    'is-downloading': mengunduhProposal[t.id] === 'loading',
                    'is-success': mengunduhProposal[t.id] === 'success',
                  }"
                  :title="mengunduhProposal[t.id] === 'loading' ? 'Sedang memproses unduhan...' : (mengunduhProposal[t.id] === 'success' ? 'Unduhan dimulai!' : 'Lihat / Unduh Juknis (PDF)')"
                  download
                  @click="saatKlikUnduhProposal(t, $event)"
                >
                  <i v-if="mengunduhProposal[t.id] === 'loading'" class="bi bi-arrow-repeat ikon-putar text-danger"></i>
                  <i v-else-if="mengunduhProposal[t.id] === 'success'" class="bi bi-check-circle-fill text-success ikon-sukses"></i>
                  <i v-else class="bi bi-file-earmark-pdf-fill text-danger"></i>
                  <span>Juknis PDF</span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Lightbox Modal untuk Melihat Poster Penuh -->
      <transition name="poster-fade">
        <div
          v-if="posterModal"
          class="poster-lightbox"
          role="dialog"
          aria-modal="true"
          @click.self="tutupPoster"
        >
          <div class="poster-lightbox__container">
            <button
              type="button"
              class="poster-lightbox__close"
              title="Tutup (Esc)"
              @click="tutupPoster"
            >
              <i class="bi bi-x-lg"></i>
            </button>

            <div class="poster-lightbox__body">
              <img
                :src="posterModal.url"
                :alt="posterModal.title"
                class="poster-lightbox__image"
              />
            </div>

            <div class="poster-lightbox__bar">
              <span class="poster-lightbox__title">{{ posterModal.title }}</span>
              <a
                :href="posterModal.url"
                target="_blank"
                download
                class="btn btn-sm btn-light d-flex align-items-center gap-1 shadow-sm"
                :class="{ 'disabled': mengunduhPoster === 'loading' }"
                @click="saatKlikUnduhPoster($event)"
              >
                <i v-if="mengunduhPoster === 'loading'" class="bi bi-arrow-repeat ikon-putar text-primary"></i>
                <i v-else-if="mengunduhPoster === 'success'" class="bi bi-check2 text-success ikon-sukses"></i>
                <i v-else class="bi bi-download"></i>
                <span>{{ mengunduhPoster === 'loading' ? 'Mengunduh...' : (mengunduhPoster === 'success' ? 'Tersimpan!' : 'Unduh Poster') }}</span>
              </a>
            </div>
          </div>
        </div>
      </transition>

      <!-- Modal Popup Jika Link Grup WA Belum Tersedia -->
      <transition name="poster-fade">
        <div
          v-if="waModal"
          class="wa-modal-overlay"
          role="dialog"
          aria-modal="true"
          @click.self="waModal = null"
        >
          <div class="wa-modal-card text-center">
            <div class="wa-modal-icon mb-3">
              <i class="bi bi-whatsapp"></i>
            </div>
            <h5 class="fw-bold text-dark mb-2">Grup WhatsApp Belum Tersedia</h5>
            <p class="text-muted small mb-4">
              Tautan grup WhatsApp untuk turnamen <strong class="text-dark">{{ waModal.tournamentName }}</strong> belum dicantumkan oleh panitia penyelenggara.
            </p>
            <button
              type="button"
              class="btn btn-primary w-100 shadow-sm"
              @click="waModal = null"
            >
              Mengerti
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import http from '@/services/http';
import { infoStatus, hanyaTerlihat } from '@/utils/status';
import { mediaUrl } from '@/utils/media';
import { useToast } from 'vue-toastification';
import { useConfirm } from '@/composables/useConfirm';

export default {
  name: 'TurnamenSayaPage',

  setup() {
    const { konfirmasi } = useConfirm();
    return { toast: useToast(), konfirmasi };
  },

  data() {
    return {
      turnamen: [],
      kontingen: [],
      terdaftar: new Set(),
      memuat: true,
      sibuk: '',
      posterModal: null,
      waModal: null,
      mengunduhProposal: {},
      mengunduhPoster: null,
    };
  },

  mounted() {
    document.title = 'Turnamen';
    this.ambilData();
    window.addEventListener('keydown', this.tanganiKeydown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.tanganiKeydown);
  },

  methods: {
    infoStatus,

    tanganiKeydown(e) {
      if (e.key === 'Escape') {
        if (this.posterModal) this.tutupPoster();
        if (this.waModal) this.waModal = null;
      }
    },

    klikGrupWA(t) {
      const link = (t.whatsapp_group || '').trim();
      if (link && link !== '-' && /^https?:\/\//i.test(link)) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        this.waModal = {
          tournamentName: t.name,
        };
        this.toast.info('Link grup WhatsApp belum tersedia dari panitia.');
      }
    },

    bukaPoster(t) {
      const url = this.gambar(t);
      if (!url) return;
      this.posterModal = {
        url,
        title: t.name,
      };
    },

    tutupPoster() {
      this.posterModal = null;
    },

    gambar(t) {
      return t.image ? mediaUrl(t.image) : null;
    },

    adaProposal(t) {
      return typeof t.document === 'string' && /\.pdf($|\?)/i.test(t.document.trim());
    },

    sisa(t) {
      const n = t?.sisa_hari_pendaftaran;
      if (n === null || n === undefined) return '';
      if (n < 0) return this.$t('tournament.deadlinePassed');
      if (n === 0) return this.$t('tournament.deadlineToday');
      return this.$t('tournament.deadlineLeft', { n });
    },

    mendesak(t) {
      const n = t?.sisa_hari_pendaftaran;
      return typeof n === 'number' && n >= 0 && n <= 7;
    },

    formatTanggal(d) {
      if (!d) return '';
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    sudahIkut(idTurnamen, idKontingen) {
      return this.terdaftar.has(`${idTurnamen}-${idKontingen}`);
    },

    async ambilData() {
      this.memuat = true;
      try {
        const [resT, resK] = await Promise.all([
          http.get('/tournaments/all'),
          http.get('/contingents/my-contingents'),
        ]);

        const daftarT = Array.isArray(resT.data) ? resT.data : resT.data?.data ?? [];
        this.turnamen = hanyaTerlihat(daftarT);
        this.kontingen = Array.isArray(resK.data) ? resK.data : resK.data?.data ?? [];

        await this.segarkanStatus();
      } catch (error) {
        // Pesannya sudah ditampilkan interceptor axios.
      } finally {
        this.memuat = false;
      }
    },

    async segarkanStatus() {
      if (!this.turnamen.length || !this.kontingen.length) return;

      const hasil = await Promise.allSettled(
        this.turnamen.map((t) =>
          http.get('/contingents/my-contingents', {
            params: { tournament_id: t.id },
            diamDiam: true,
          })
        )
      );

      const set = new Set();
      hasil.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        const idT = this.turnamen[i].id;
        const daftar = Array.isArray(r.value.data) ? r.value.data : [];
        daftar.forEach((k) => {
          if (k.is_registered) set.add(`${idT}-${k.id}`);
        });
      });
      this.terdaftar = set;
    },

    async daftarkan(t, k) {
      const setuju = await this.konfirmasi({
        title: 'Ikutkan kontingen ke acara ini?',
        message: `${k.name} akan didaftarkan ke ${t.name}.`,
        confirmText: 'Ya, ikutkan',
      });
      if (!setuju) return;

      this.sibuk = `${t.id}-${k.id}`;
      try {
        await http.post('/tournaments/register', {
          tournament_id: t.id,
          contingents: [k.id],
        });

        this.terdaftar = new Set([...this.terdaftar, `${t.id}-${k.id}`]);
        this.toast.success(`${k.name} berhasil diikutkan.`);
      } catch (error) {
        // Pesannya sudah ditampilkan interceptor axios.
      } finally {
        this.sibuk = '';
      }
    },

    urlProposal(t) {
      if (!t?.document) return '#';
      return mediaUrl(t.document) || t.document;
    },

    saatKlikUnduhProposal(t, event) {
      if (this.mengunduhProposal[t.id] === 'loading') {
        if (event) event.preventDefault();
        return;
      }

      this.mengunduhProposal = { ...this.mengunduhProposal, [t.id]: 'loading' };
      this.toast.info('Memulai unduhan berkas proposal... Mohon tunggu sebentar.');

      // Berikan jeda animasi untuk proses stream unduhan oleh peramban
      setTimeout(() => {
        this.mengunduhProposal = { ...this.mengunduhProposal, [t.id]: 'success' };
        setTimeout(() => {
          const salinan = { ...this.mengunduhProposal };
          delete salinan[t.id];
          this.mengunduhProposal = salinan;
        }, 2500);
      }, 3500);
    },

    saatKlikUnduhPoster(event) {
      if (this.mengunduhPoster === 'loading') {
        if (event) event.preventDefault();
        return;
      }

      this.mengunduhPoster = 'loading';
      setTimeout(() => {
        this.mengunduhPoster = 'success';
        setTimeout(() => {
          this.mengunduhPoster = null;
        }, 2500);
      }, 2000);
    },

    unduhProposal(t) {
      const url = this.urlProposal(t);
      if (!url || url === '#') return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style scoped>
.page-main-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
}

/* ── Container Grid (Portrait Showcase) ── */
.poster-turnamen-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 300px));
  gap: 1.5rem;
  align-items: start;
}

/* ── Full Portrait Card ── */
.poster-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 380px;
  background: var(--bg-surface, #ffffff);
  border: 1px solid var(--border-subtle, #e2e8f0);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease, border-color 0.25s ease;
}

.poster-card:hover {
  transform: translateY(-4px);
  border-color: var(--brand-500, #ea580c);
  box-shadow: 0 12px 30px rgba(234, 88, 12, 0.12);
}

/* ── Poster Media Container (Top Full Portrait) ── */
.poster-card__media {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: var(--bg-sunken, #0f172a);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid var(--border-subtle, #e2e8f0);
}

.poster-card__media.has-image {
  cursor: pointer;
}

.poster-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
  transition: transform 0.35s ease;
}

.poster-card:hover .poster-card__img {
  transform: scale(1.04);
}

.poster-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1e293b, #0f172a);
}

/* Hover overlay with zoom hint */
.poster-card__overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(15, 23, 42, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.poster-card:hover .poster-card__overlay {
  opacity: 1;
}

.poster-card__zoom-btn {
  background: rgba(255, 255, 255, 0.94);
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.38rem 0.85rem;
  border-radius: 999px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  display: inline-flex;
  align-items: center;
  transform: translateY(4px);
  transition: transform 0.2s ease;
}

.poster-card:hover .poster-card__zoom-btn {
  transform: translateY(0);
}

/* Floating Badges on Poster */
.floating-badge-top-right {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
}

.mini-status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
}

.status--success {
  background: rgba(16, 185, 129, 0.94);
  color: #ffffff;
}

.status--warning {
  background: rgba(245, 158, 11, 0.94);
  color: #ffffff;
}

.status--danger {
  background: rgba(239, 68, 68, 0.94);
  color: #ffffff;
}

.status--neutral {
  background: rgba(100, 116, 139, 0.94);
  color: #ffffff;
}

.badge-sisa-hari {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 20px;
  background: rgba(234, 88, 12, 0.12);
  color: #ea580c;
  display: inline-flex;
  align-items: center;
}

.badge-sisa-hari.is-urgent {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  font-weight: 700;
}

/* ── Card Body ── */
.poster-card__body {
  padding: 0.85rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  gap: 0.7rem;
}

.badge-kejuaraan-resmi {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 20px;
  background: var(--bg-sunken, #f1f5f9);
  color: var(--text-secondary, #64748b);
  border: 1px solid var(--border-subtle, #e2e8f0);
  display: inline-flex;
  align-items: center;
}

.poster-card__title {
  font-size: 1.18rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-top: 0.35rem;
  margin-bottom: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Info Rows List ── */
.poster-card__info-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.6rem 0.75rem;
  background: var(--bg-sunken, #f8fafc);
  border: 1px solid var(--border-subtle, #e2e8f0);
  border-radius: 10px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.82rem;
}

.info-row__icon {
  font-size: 0.95rem;
  flex-shrink: 0;
  line-height: 1.25;
}

.info-row__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.info-row__label {
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.info-row__value {
  font-weight: 600;
  color: var(--text-primary, #0f172a);
  line-height: 1.25;
  word-break: break-word;
}

/* ── Footer Actions ── */
.poster-card__footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
}

.poster-card__footer .btn {
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 8px;
  min-height: 44px;
  padding: 0.55rem 0.85rem;
}

/* ── Tombol WhatsApp ── */
.btn-wa {
  background: rgba(37, 211, 102, 0.08);
  color: #15803d;
  border: 1px solid rgba(37, 211, 102, 0.35);
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-wa:hover {
  background: #25d366;
  color: #ffffff;
  border-color: #25d366;
  box-shadow: 0 4px 14px rgba(37, 211, 102, 0.25);
}

.btn-wa i {
  font-size: 1.05rem;
  color: #25d366;
  transition: color 0.2s ease;
}

.btn-wa:hover i {
  color: #ffffff;
}

/* ── WhatsApp Modal Dialog ── */
.wa-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.wa-modal-card {
  background: var(--bg-surface, #ffffff);
  border: 1px solid var(--border-subtle, #e2e8f0);
  border-radius: 18px;
  padding: 2rem 1.75rem 1.5rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  animation: pop-masuk 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.wa-modal-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(37, 211, 102, 0.12);
  color: #25d366;
  font-size: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* ── Tombol Unduh Loading & Sukses ── */
.btn.is-downloading {
  cursor: wait !important;
  pointer-events: none;
  background: var(--bg-sunken, #f1f5f9) !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
}

.btn.is-success {
  background: rgba(16, 185, 129, 0.12) !important;
  border-color: rgba(16, 185, 129, 0.4) !important;
}

@keyframes putar-halus {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.ikon-putar {
  animation: putar-halus 0.85s linear infinite;
  display: inline-block;
}

@keyframes pop-masuk {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.25); }
  100% { transform: scale(1); opacity: 1; }
}

.ikon-sukses {
  animation: pop-masuk 0.28s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  display: inline-block;
}

/* ── Empty & Skeleton ── */
.empty-box {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 3rem 1.5rem;
}

.empty-icon-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--bg-sunken, #f8fafc);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.skeleton-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 360px;
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  overflow: hidden;
}

.skeleton-poster {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: var(--bg-sunken, #f1f5f9);
}

.skeleton-line {
  background: var(--bg-sunken, #f1f5f9);
  border-radius: 4px;
}

/* ── Lightbox Modal ── */
.poster-lightbox {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.88);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.poster-lightbox__container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90vw;
  max-height: 92vh;
}

.poster-lightbox__close {
  position: absolute;
  top: -42px;
  right: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #ffffff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.poster-lightbox__close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.poster-lightbox__body {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 80vh;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  background: #000;
}

.poster-lightbox__image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  display: block;
}

.poster-lightbox__bar {
  margin-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  color: #ffffff;
  padding: 0 0.5rem;
}

.poster-lightbox__title {
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

/* Lightbox Transitions */
.poster-fade-enter-active,
.poster-fade-leave-active {
  transition: opacity 0.25s ease;
}

.poster-fade-enter-from,
.poster-fade-leave-to {
  opacity: 0;
}

@media (max-width: 576px) {
  .poster-turnamen-grid {
    grid-template-columns: 1fr;
  }
  .poster-card {
    max-width: 100%;
  }
}
</style>

